import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import useSelectedFilterLabels from '../../../hooks/useSelectedFilterLabels';
import { useDepotsFiltersQuery } from '../useDepotsFiltersQuery';

interface UseTeamFiltersProps {
  value: string[];
  onSubmit: (data: SearchableFormValues) => void;
  oemSegmentsIds?: string[];
  sentinelTeamsIds?: string[];
  platformAccountIds?: string[];
}

export default function useDepotsFilters({
  value,
  oemSegmentsIds = [],
  sentinelTeamsIds = [],
  platformAccountIds = [],
  onSubmit,
}: UseTeamFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: depotsData,
    loading,
    loadMore,
    loadingMore,
  } = useDepotsFiltersQuery({
    variables: {
      page: 1,
      text: debouncedSearchValue,
      oemSegmentsIds,
      sentinelTeamsIds,
      platformAccountIds,
    },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      (depotsData?.managedAssetGroupings.collection ?? []).map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [depotsData],
  );

  const labelsById = useSelectedFilterLabels(items);

  // When a single depot is pre-selected but is not part of the currently
  // loaded results (e.g. it lives on a later page), fetch it by id so the
  // filter button can show its name instead of the generic fallback.
  const selectedId = value.length === 1 ? value[0] : undefined;

  const { data: selectedData } = useDepotsFiltersQuery({
    variables: {
      page: 1,
      text: '',
      ids: selectedId ? [selectedId] : [],
      oemSegmentsIds: [],
      sentinelTeamsIds: [],
      platformAccountIds: [],
    },
    skip:
      !selectedId ||
      !depotsData ||
      items.some((item) => item.value === selectedId) ||
      Boolean(labelsById[selectedId]),
  });

  const selectedLabel = selectedData?.managedAssetGroupings.collection.find(
    (record) => record.id === selectedId,
  )?.name;

  const text = useMemo(() => {
    if (value.length === 0) return t`All Depots`;

    if (value.length === 1)
      return (
        labelsById[value[0]] ||
        items.find((item) => item.value === value[0])?.label ||
        selectedLabel ||
        t`Depot`
      );

    return t`Depots (${value.length})`;
  }, [labelsById, items, selectedLabel, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
